/**
 * Translations Tab (system templates only).
 *
 * Restored from the client-approved design in `translation-tab-old.tsx`:
 * a single card with status badge, two action buttons, a compact
 * coverage strip (see `TranslationTabStats`), the template-level DE
 * translation form, and the key references at the bottom.
 *
 * What changed under the hood:
 *   - The old `/api/admin/translations/*` Next.js endpoints are gone.
 *     Diff data + module discovery now come from `useTemplateUilmDiff`,
 *     and writes go through `uilm-admin.service.ts` directly.
 *   - "Stats" is a one-line strip with a "View details" dialog — keeps
 *     the header dense.
 *   - Title / description editing uses the UILM resources surfaced by
 *     the diff hook; saving issues two `Key/Save` calls + one publish.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Languages,
  Loader2,
  MoreVertical,
  RefreshCw,
  XCircle,
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Textarea } from '@/components/ui-kit/textarea';
import { useToast } from '@/hooks/use-toast';
import { useLatestTranslationJob } from '../../hooks/use-latest-translation-job';
import { useTemplateUilmDiff } from '../../hooks/use-template-uilm-diff';
import { clearUnpublished } from '../../lib/unpublished-tracker';
import { saveUilmKeys, type UilmKey } from '../../services/uilm-admin.service';
import { TranslationTabSeedAction } from './translation-tab-seed-action';
import { TranslationTabStats } from './translation-tab-stats';
import { TranslationTabTranslateAction } from './translation-tab-translate-action';

interface TranslationsTabProps {
  templateId: string;
  /** Other props kept for backward-compatibility with the existing caller. */
  templateSlug?: string;
  templateTitle?: string;
  templateDescription?: string;
  uilmTitleKey?: string | null;
  uilmDescriptionKey?: string | null;
}

type SyncStatus = 'loading' | 'not-in-uilm' | 'en-only' | 'synced' | 'error';

export function TranslationsTab({
  templateId,
  templateTitle = '',
  templateDescription = '',
}: TranslationsTabProps) {
  const { toast } = useToast();
  const { data: diff, isLoading, isFetching, error, refetch } = useTemplateUilmDiff(templateId);

  // Auto-translate job state. The hook auto-polls every 5s while a job
  // is `running` and stops once it terminates. We only watch the
  // most-recent job per template — older ones are surfaced as history
  // elsewhere if we ever build a job log.
  const { data: latestJob, refetch: refetchLatestJob } = useLatestTranslationJob(templateId);

  // When the job flips from running → terminal, refetch the diff so the
  // freshly-translated DE values appear in the form. We compare against
  // the previous status via a ref to fire exactly once per transition.
  const prevJobStatus = useRef<string | null>(null);
  useEffect(() => {
    const current = latestJob?.status ?? null;
    const previous = prevJobStatus.current;
    prevJobStatus.current = current;
    if (previous === 'running' && (current === 'completed' || current === 'failed')) {
      refetch();
      if (current === 'completed') {
        toast({
          variant: 'success',
          title: 'Auto-translation finished',
          description: 'DE values are now available — they will appear below.',
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Auto-translation failed',
          description: latestJob?.errorMessage ?? 'The job did not complete successfully.',
        });
      }
    }
  }, [latestJob, refetch, toast]);

  // Local DE edits — initialized from the UILM remote resources, kept in
  // local state so admins can edit without round-tripping on every keystroke.
  const [titleDe, setTitleDe] = useState('');
  const [descriptionDe, setDescriptionDe] = useState('');
  const [saving, setSaving] = useState(false);

  // Find the template-level keys + their current UILM values.
  const templateKeyInfo = useMemo(() => {
    if (!diff) return null;
    const titleEntry = diff.localPlan.find(
      (p) => p.ownerType === 'template' && p.uilmField === 'uilmTitleKey'
    );
    const descEntry = diff.localPlan.find(
      (p) => p.ownerType === 'template' && p.uilmField === 'uilmDescriptionKey'
    );
    const titleRemote = titleEntry ? findRemote(diff.remoteKeys, titleEntry.keyName) : undefined;
    const descRemote = descEntry ? findRemote(diff.remoteKeys, descEntry.keyName) : undefined;
    return {
      titleKey: titleEntry?.keyName,
      descKey: descEntry?.keyName,
      titleEn: titleEntry?.enValue ?? templateTitle,
      descEn: descEntry?.enValue ?? templateDescription,
      titleDe: pickResource(titleRemote, 'de-DE'),
      descDe: pickResource(descRemote, 'de-DE'),
    };
  }, [diff, templateTitle, templateDescription]);

  // Reset the DE form whenever new diff data arrives.
  useEffect(() => {
    if (!templateKeyInfo) return;
    setTitleDe(templateKeyInfo.titleDe);
    setDescriptionDe(templateKeyInfo.descDe);
  }, [templateKeyInfo]);

  const status: SyncStatus = useMemo(() => {
    if (isLoading) return 'loading';
    if (error || !diff) return 'error';
    if (!templateKeyInfo?.titleKey) return 'not-in-uilm';
    // If the title key isn't on UILM yet we'd see it in `missing`.
    const titleMissing = diff.missing.some((p) => p.keyName === templateKeyInfo.titleKey);
    if (titleMissing) return 'not-in-uilm';
    if (!templateKeyInfo.titleDe || !templateKeyInfo.descDe) return 'en-only';
    return 'synced';
  }, [diff, error, isLoading, templateKeyInfo]);

  const handleSave = async () => {
    if (!diff || !templateKeyInfo?.titleKey || !templateKeyInfo?.descKey) {
      toast({
        variant: 'destructive',
        title: 'Cannot save yet',
        description: 'Run Seed first so the template-level keys exist on UILM.',
      });
      return;
    }
    setSaving(true);
    try {
      // Save both title + description with EN (from local DB) + DE (form) in
      // one batch. `saveUilmKeys` publishes once at the end, so dropping the
      // explicit `generateUilmFile` here is intentional. `isNewKey: false`
      // — the keys must exist by the time the Save button is enabled
      // (status !== 'not-in-uilm'), so this is always an update.
      await saveUilmKeys(diff.moduleId, [
        {
          keyName: templateKeyInfo.titleKey,
          resources: [resource(templateKeyInfo.titleEn, 'en-US'), resource(titleDe, 'de-DE')],
          isNewKey: false,
        },
        {
          keyName: templateKeyInfo.descKey,
          resources: [resource(templateKeyInfo.descEn, 'en-US'), resource(descriptionDe, 'de-DE')],
          isNewKey: false,
        },
      ]);
      clearUnpublished(diff.template.slug);
      await refetch();
      toast({ variant: 'success', title: 'Translations saved' });
    } catch (err) {
      console.error('Save failed:', err);
      toast({
        variant: 'destructive',
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Could not save translations.',
      });
    } finally {
      setSaving(false);
    }
  };

  // --- Render guards ---

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 flex flex-col items-center gap-3 text-globe-grey">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-sm">Comparing local template with UILM…</span>
        </CardContent>
      </Card>
    );
  }
  if (error || !diff) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <CardTitle className="text-base">UILM diff failed</CardTitle>
          </div>
          <CardDescription>
            {error instanceof Error
              ? error.message
              : 'Could not load UILM state for this template.'}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="p-4 sm:p-6">
          {/* Header row — stacks on mobile so the wide Seed/Translate
              buttons don't squeeze the title. */}
          <div className="flex  gap-3 flex-row items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <Languages className="h-5 w-5 shrink-0 text-poly-green" />
              <CardTitle className="text-base sm:text-lg truncate">Template Translations</CardTitle>
              <StatusBadge status={status} />
            </div>
            {/* Desktop: inline Seed / Translate / Refresh buttons. */}
            <div className="hidden md:flex flex-wrap items-center gap-2">
              <TranslationTabSeedAction
                templateId={templateId}
                missingCount={diff.missing.length}
                onSeeded={() => refetch()}
              />
              <TranslationTabTranslateAction
                moduleId={diff.moduleId}
                templateId={templateId}
                templateSlug={diff.template.slug}
                hasRemoteKeys={diff.remoteKeys.length > 0}
                isJobRunning={latestJob?.status === 'running'}
                onJobStarted={() => refetchLatestJob()}
              />

              <Button
                variant="primaryOutline"
                className="shrink-0"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
                title="Refresh UILM diff"
                aria-label="Refresh UILM diff"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* Mobile: collapse all three actions into a 3-dot menu. The
                Seed/Translate sub-components render as DropdownMenuItems
                via `asMenuItem`, so their loading + disabled logic stays
                in one place. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="md:hidden">
                <Button variant="outline" size="sm" className="" aria-label="Translation actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <TranslationTabSeedAction
                  asMenuItem
                  templateId={templateId}
                  missingCount={diff.missing.length}
                  onSeeded={() => refetch()}
                />
                <TranslationTabTranslateAction
                  asMenuItem
                  moduleId={diff.moduleId}
                  templateId={templateId}
                  templateSlug={diff.template.slug}
                  hasRemoteKeys={diff.remoteKeys.length > 0}
                  isJobRunning={latestJob?.status === 'running'}
                  onJobStarted={() => refetchLatestJob()}
                />
                <DropdownMenuItem
                  disabled={isFetching}
                  onSelect={(e) => {
                    e.preventDefault();
                    refetch();
                  }}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
                  Refresh
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <CardDescription className="mt-2 text-xs sm:text-sm">
            EN values are seeded automatically when the template is created. DE values can be
            auto-translated or edited manually below.
          </CardDescription>
        </CardHeader>

        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-4 sm:space-y-6">
          {/* Auto-translate job status — only render while a job is in
              flight or just finished. The user can dismiss completed/failed
              by triggering another action; the banner is informational only. */}
          {latestJob?.status === 'running' && (
            <Banner tone="info">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              <span>
                <strong>Auto-translation in progress.</strong> UILM is translating this template in
                the background — the DE values below will update automatically when it finishes.
              </span>
            </Banner>
          )}
          {latestJob?.status === 'failed' && (
            <Banner tone="error">
              <XCircle className="h-4 w-4 shrink-0" />
              <span>
                <strong>Last auto-translation failed.</strong>{' '}
                {latestJob.errorMessage ?? 'Click Auto-Translate to retry.'}
              </span>
            </Banner>
          )}

          {status === 'not-in-uilm' && (
            <Banner>
              No UILM keys found for this template yet. Click <strong>Seed UILM Keys</strong> above
              to create them.
            </Banner>
          )}
          {status === 'en-only' && latestJob?.status !== 'running' && (
            <Banner>
              German (DE) translations are missing. Click <strong>Auto-translate to DE</strong>, or
              enter values below and save.
            </Banner>
          )}

          <TranslationTabStats diff={diff} latestJob={latestJob} onReconciled={() => refetch()} />

          {/* Title */}
          <Section title="Title">
            <ReadOnlyField label="English (EN)" value={templateKeyInfo?.titleEn ?? ''} />
            <EditableField
              label="German (DE)"
              value={titleDe}
              onChange={setTitleDe}
              placeholder="Template title in German"
            />
          </Section>

          {/* Description */}
          <Section title="Description">
            <ReadOnlyField label="English (EN)" value={templateKeyInfo?.descEn ?? ''} multiline />
            <EditableField
              label="German (DE)"
              value={descriptionDe}
              onChange={setDescriptionDe}
              placeholder="Template description in German"
              multiline
            />
          </Section>

          {/* Key references — overflow-x-auto + break-all so long UILM
              keys stay readable on phones without breaking the layout. */}
          {templateKeyInfo?.titleKey && (
            <div className="text-xs text-globe-grey p-2 bg-muted/30 rounded font-mono space-y-1 overflow-x-auto">
              <p className="break-all">
                Title key: <span className="text-[hsl(var(--fg))]">{templateKeyInfo.titleKey}</span>
              </p>
              {templateKeyInfo.descKey && (
                <p className="break-all">
                  Description key:{' '}
                  <span className="text-[hsl(var(--fg))]">{templateKeyInfo.descKey}</span>
                </p>
              )}
              <p className="break-all">
                Module ID: <span className="text-[hsl(var(--fg))]">{diff.moduleId}</span>
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || status === 'not-in-uilm' || status === 'loading'}
              className="w-full sm:w-auto"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Translations
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function findRemote(remoteKeys: UilmKey[], keyName: string): UilmKey | undefined {
  return remoteKeys.find((k) => k.keyName === keyName);
}

function pickResource(key: UilmKey | undefined, culture: string): string {
  return key?.resources.find((r) => r.culture === culture)?.value ?? '';
}

function resource(value: string, culture: string) {
  return { value, culture, characterLength: value.length };
}

// ---------------------------------------------------------------------------
// Small render primitives — kept inline because they're only used here.
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: SyncStatus }) {
  if (status === 'loading') return null;
  if (status === 'error')
    return (
      <Badge variant="destructive" className="text-xs">
        Error
      </Badge>
    );
  if (status === 'not-in-uilm')
    return (
      <Badge variant="outline" className="text-xs text-globe-grey">
        Not in UILM
      </Badge>
    );
  if (status === 'en-only')
    return (
      <Badge variant="secondary" className="text-xs bg-red-200 text-red-800">
        EN only
      </Badge>
    );
  return (
    <Badge variant="secondary" className="text-xs bg-lime-green/10 text-poly-green">
      <CheckCircle2 className="h-3 w-3 mr-1" />
      Synced
    </Badge>
  );
}

type BannerTone = 'warning' | 'info' | 'error';

const BANNER_TONE_CLASSES: Record<BannerTone, string> = {
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  info: 'bg-selise-blue/10 border-selise-blue/30 text-selise-blue',
  error: 'bg-destructive/10 border-destructive/30 text-destructive',
};

function Banner({ children, tone = 'warning' }: { children: React.ReactNode; tone?: BannerTone }) {
  // Info/error banners render their own leading icon inline (spinner,
  // X-circle) so we suppress the default AlertCircle in those tones.
  const showDefaultIcon = tone === 'warning';
  return (
    <div
      className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${BANNER_TONE_CLASSES[tone]}`}
    >
      {showDefaultIcon && <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />}
      <div className="flex items-start gap-2 flex-1">{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2 sm:space-y-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      {/* Stack EN/DE on mobile so each language gets full-width breathing
          room; 2-column side-by-side comparison on sm+. */}
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function ReadOnlyField({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-globe-grey">{label}</Label>
      <p
        className={`text-sm px-3 py-2 rounded-md border bg-muted/40 text-globe-grey whitespace-pre-wrap ${
          multiline ? 'min-h-18' : 'min-h-9'
        }`}
      >
        {value || <span className="italic">No value</span>}
      </p>
    </div>
  );
}

function EditableField({
  label,
  value,
  onChange,
  placeholder,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  multiline?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-globe-grey">{label}</Label>
      {multiline ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
        />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
}
